<template>
  <v-container>
    <v-row no-gutters justify="center">
      <v-col cols="12" sm="10" md="10" lg="8" xl="6">
        <v-card outlined>
          <v-card-title
            v-text="$t('signUp.title')"
            class="font-weight-bold primary--text"
          ></v-card-title>
          <v-divider></v-divider>
          <v-progress-linear color="secondary" v-if="false"></v-progress-linear>
          <!-- Validate registration token -->
          <template v-if="uuidValidation.loading">
            <v-row class="fill-height" align-content="center" justify="center">
              <v-col
                class="text-h6 text-center"
                cols="12"
                v-text="$t('signUp.waintingSignupValidationUrl')"
              >
              </v-col>
              <v-col cols="6">
                <v-progress-linear
                  color="secondary"
                  :indeterminate="uuidValidation.loading"
                  rounded
                  height="6"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </template>
          <!-- Registration registration process -->
          <v-row
            no-gutters
            justify="center"
            v-else-if="uuidValidation.valid && !userAndCreatorCreated"
          >
            <v-progress-linear
              color="secondary"
              :indeterminate="true"
              rounded
              height="6"
              v-if="formLoading"
            ></v-progress-linear>
            <v-form
              ref="createUserForm"
              v-model="validUserForm"
              lazy-validation
              class="pt-4 px-4"
            >
              <!-- Username Input -->
              <v-text-field
                id="usernameInput"
                name="username"
                v-bind:label="$t('signUp.username')"
                type="text"
                v-model="user.username"
                required
                :rules="usernameRules"
                ref="usernameInput"
                :disabled="formLoading"
                :error-messages="$t(errors.usernameInput)"
                @focus="removeInputErrorMessage('usernameInput')"
                color="primary"
                outlined
                append-icon="fas fa-at"
              >
              </v-text-field>
              <!-- Email Input -->

              <v-text-field
                id="emailInput"
                name="email"
                v-bind:label="$t('signUp.email')"
                type="email"
                v-model="user.email"
                :rules="emailRules"
                required
                :disabled="formLoading"
                ref="emailInput"
                :error-messages="$t(errors.emailInput)"
                @focus="removeInputErrorMessage('emailInput')"
                color="primary"
                outlined
                append-icon="fas fa-envelope"
              >
              </v-text-field>

              <!-- Password Input -->
              <v-text-field
                id="passwordInput"
                outlined
                name="password"
                v-bind:label="$t('signUp.password')"
                :type="showPassword ? 'text' : 'password'"
                color="primary"
                v-model="user.password"
                :rules="passwordRules"
                required
                :disabled="formLoading"
                ref="passwordInput"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                :error-messages="$t(errors.passwordInput)"
                @focus="removeInputErrorMessage('passwordInput')"
                @input="removeInputErrorMessage('passwordInput')"
              >
              </v-text-field>
              <!-- ConfirmPassword Input -->
              <v-text-field
                outlined
                name="confirmPassword"
                v-bind:label="$t('signUp.confirmPassword')"
                :type="showPassword ? 'text' : 'password'"
                color="primary"
                :rules="confirmPasswordRules"
                v-model="user.confirmPassword"
                required
                :disabled="formLoading"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
              >
              </v-text-field>
              <!-- Password Validation Input -->
              <div class="px-2 pb-4">
                <!-- At least 8 characters -->
                <div class="passwordTip">
                  <v-icon
                    :color="
                      passwordAtLeastEightCharacters ? 'success' : user.password.length > 0 ? 'error' : '',
                    "
                    v-text="passwordAtLeastEightCharacters ? 'done' : user.password.length > 0 ? 'warning' : 'info',"
                    class="pr-2"
                  >
                  </v-icon>
                  <span
                    v-text="$t('account.rules.password.atLeastEigthCharacters')"
                    class="text-caption grey--text"
                    v-bind:class="{
                      'success--text': passwordAtLeastEightCharacters,
                      'error--text':
                        !passwordAtLeastEightCharacters &&
                        user.password.length > 0
                    }"
                  ></span>
                </div>
                <!-- At least one Number -->
                <div class="passwordTip">
                  <v-icon
                    :color="
                      passwordAtleastOneNumber ? 'success' : user.password.length > 0 ? 'error' : '',
                    "
                    v-text="passwordAtleastOneNumber ? 'done' : user.password.length > 0 ? 'warning' : 'info',"
                    class="pr-2"
                  >
                  </v-icon>
                  <span
                    v-text="$t('account.rules.password.atleastOneNumber')"
                    class="text-caption grey--text"
                    v-bind:class="{
                      'success--text': passwordAtleastOneNumber,
                      'error--text':
                        !passwordAtleastOneNumber && user.password.length > 0
                    }"
                  ></span>
                </div>
                <!-- At least one lowercase letter -->
                <div class="passwordTip">
                  <v-icon
                    :color="
                      passwordAtleastOneLowercaseLetter ? 'success' : user.password.length > 0 ? 'error' : '',
                    "
                    v-text="passwordAtleastOneLowercaseLetter ? 'done' : user.password.length > 0 ? 'warning' : 'info',"
                    class="pr-2"
                  >
                  </v-icon>
                  <span
                    v-text="
                      $t('account.rules.password.atLeastOneLowercaseLetter')
                    "
                    class="text-caption grey--text"
                    v-bind:class="{
                      'success--text': passwordAtleastOneLowercaseLetter,
                      'error--text':
                        !passwordAtleastOneLowercaseLetter &&
                        user.password.length > 0
                    }"
                  ></span>
                </div>
                <!-- At least One Capital letter -->
                <div class="passwordTip">
                  <v-icon
                    :color="
                      passwordAtleastOneCapitalLetter ? 'success' : user.password.length > 0 ? 'error' : '',
                    "
                    v-text="passwordAtleastOneCapitalLetter ? 'done' : user.password.length > 0 ? 'warning' : 'info',"
                    class="pr-2"
                  >
                  </v-icon>
                  <span
                    v-text="
                      $t('account.rules.password.atleastOneCapitalLetter')
                    "
                    class="text-caption grey--text"
                    v-bind:class="{
                      'success--text': passwordAtleastOneCapitalLetter,
                      'error--text':
                        !passwordAtleastOneCapitalLetter &&
                        user.password.length > 0
                    }"
                  ></span>
                </div>
                <!-- Password must match -->
                <div class="passwordTip">
                  <v-icon
                    :color="
                      user.password.length > 0 &&
                      user.confirmPassword.length > 0
                        ? passwordAndConfirmPasswordAreEqual
                          ? 'success'
                          : 'error'
                        : ''
                    "
                    v-text="
                      user.password.length > 0 &&
                      user.confirmPassword.length > 0
                        ? passwordAndConfirmPasswordAreEqual
                          ? 'done'
                          : 'warning'
                        : 'info'
                    "
                    class="pr-2"
                  >
                  </v-icon>
                  <span
                    v-text="
                      $t(
                        'account.rules.password.passwordAndConfirmPasswordArentEqual'
                      )
                    "
                    class="text-caption grey--text"
                    v-bind:class="{
                      'success--text':
                        user.password.length > 0 &&
                        user.confirmPassword.length > 0 &&
                        passwordAndConfirmPasswordAreEqual,
                      'error--text':
                        user.password.length > 0 &&
                        user.confirmPassword.length > 0 &&
                        !passwordAndConfirmPasswordAreEqual
                    }"
                  ></span>
                </div>
              </div>
            </v-form>
            <v-col cols="12"><v-divider></v-divider></v-col>
            <v-col cols="12" class="pa-4 d-flex flex-column">
              <v-btn
                large
                block
                color="primary"
                @click.stop="registerMe()"
                :disabled="!validUserForm"
                :loading="formLoading"
              >
                {{ $t("signUp.signUp") }}
              </v-btn>
              <div class="text-center pt-2">
                <span>
                  {{ $t("signUp.AlreadyHaveAppTitleAccount") }}
                </span>
                <a
                  v-on:click="
                    $store.dispatch('navigation/closeSignUpView');
                    $store.dispatch('navigation/openLoginView');
                  "
                >
                  <span class="LoginText">
                    {{ $t("signUp.login") }}
                  </span>
                </a>
              </div>
            </v-col>
          </v-row>
          <!-- Success -->
          <template v-else-if="userAndCreatorCreated">
            <v-row
              align-content="center"
              justify="center"
              no-gutters
              class="pa-4"
            >
              <v-col cols="6" class="text-center pb-4">
                <v-icon large color="success" v-text="'done_outline'"></v-icon>
              </v-col>
              <v-col
                class="text-body-1 text-center pb-4"
                cols="8"
                v-text="$t('account.accountWaitingForActivationPart1')"
              >
              </v-col>
              <v-col
                class="text-h6 text-center pb-4"
                cols="8"
                v-text="user.email"
              ></v-col>
              <v-col
                class="text-body-1 text-center pb-4"
                cols="8"
                v-text="$t('account.accountWaitingForActivationPart2')"
              >
              </v-col>
              <v-col
                class="text-h6 primary--text text-center pb-4 font-weight-bold"
                cols="8"
                v-text="$t('account.accountWaitingForActivationPart3')"
              >
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <v-row
              align-content="center"
              justify="center"
              no-gutters
              class="pa-4"
            >
              <v-col cols="6" class="text-center pb-4">
                <v-icon large color="error" v-text="'error'"></v-icon>
              </v-col>
              <v-col
                class="text-h6 text-center pb-4"
                cols="12"
                v-text="$t('account.wrongRegistrationToken')"
              >
              </v-col>
              <router-link :to="{ name: 'home' }">
                <v-btn
                  color="secondary"
                  depressed
                  v-text="$t('account.goBackToGemmici')"
                ></v-btn>
              </router-link>
            </v-row>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AccountService from "../../account/services/AccountService";
import ErrorInputMixins from "@/services/ErrorInputMixins";
export default {
  mixins: [ErrorInputMixins],
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      validUserForm: true,
      uuidValidation: {
        valid: false,
        loading: true
      },
      formLoading: false,
      user: {
        username: "",
        email: "",
        password: "",
        confirmPassword: ""
      },
      showPassword: false,
      userAndCreatorCreated: false,
      /* Input Form rules */
      usernameRules: [
        v => !!v || this.$i18n.t("signUp.rules.username.required"),
        v => /^\S*$/.test(v) || this.$i18n.t("signUp.rules.username.noSpace"),
        v =>
          /^[a-z0-9@+\-._]+$/gi.test(v) ||
          this.$i18n.t("signUp.rules.username.specialCaracters"),
        v => v.length >= 1 || this.$i18n.t("signUp.rules.username.min"),
        v => v.length <= 50 || this.$i18n.t("signUp.rules.username.max")
      ],
      emailRules: [
        v => !!v || this.$i18n.t("signUp.rules.email.required"),
        v => /.+@.+\..+/.test(v) || this.$i18n.t("signUp.rules.email.valid")
      ],
      passwordRules: [
        v => !!v || this.$i18n.t("account.rules.password.required"),
        () => this.passwordAtLeastEightCharacters || "",
        () => this.passwordAtleastOneNumber || "",
        () => this.passwordAtleastOneLowercaseLetter || "",
        () => this.passwordAtleastOneCapitalLetter || "",
        () =>
          this.passwordNoSpace ||
          this.$i18n.t("account.rules.password.noSpace"),
        () => this.passwordAndConfirmPasswordAreEqual || ""
      ],
      confirmPasswordRules: [
        v => !!v || this.$i18n.t("account.rules.password.required")
      ]
    };
  },
  computed: {
    passwordAtLeastEightCharacters() {
      return /^.*(?=.{8,}).*$/.test(this.user.password);
    },
    passwordAtleastOneNumber() {
      return /^.*(?=.*\d).*$/.test(this.user.password);
    },
    passwordAtleastOneCapitalLetter() {
      return /^.*((?=.*[A-Z]){1}).*/.test(this.user.password);
    },
    passwordAtleastOneLowercaseLetter() {
      return /^.*((?=.*[a-z]){1}).*/.test(this.user.password);
    },
    passwordNoSpace() {
      return /^\S*$/.test(this.user.password);
    },
    passwordAndConfirmPasswordAreEqual() {
      if (
        this.user.password.length > 0 &&
        this.user.confirmPassword.length > 0 &&
        this.user.password !== this.user.confirmPassword
      )
        return false;
      return true;
    }
  },
  methods: {
    registerMe() {
      if (this.$refs.createUserForm.validate()) {
        this.formLoading = true;
        AccountService.registerNewUser({ ...this.user })
          .then(() =>
            AccountService.registerNewCreator(this.user.username, this.uuid)
              .then(() => (this.userAndCreatorCreated = true))
              .catch()
              .finally((this.formLoading = false))
          )
          .catch(error => {
            this.triggerInputAlert(error);
            this.formLoading = false;
          });
      }
    }
  },
  created() {
    /* Valide registration code in url */
    AccountService.checkCreatorRegistrationCode(this.uuid)
      .then(() => (this.uuidValidation.valid = true))
      .catch(() => (this.uuidValidation.valid = false))
      .finally(() => (this.uuidValidation.loading = false));
  }
};
</script>
<style>
.passwordTip {
  display: flex;
  align-items: flex-start;
  margin-bottom: 4px;
}
</style>
